import React, { useState, useEffect } from 'react';
import { ref, onValue, query, orderByChild, equalTo } from 'firebase/database';
import { database } from '../firebase';
import moment from 'moment';
import Header from './Header';
import Footer from './Footer';

const SchoolInformationPage = () => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [parentData, setParentData] = useState(null);
  const [mobileVerified, setMobileVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [calendars, setCalendars] = useState([]);
  const [loadingCalendars, setLoadingCalendars] = useState(false);

  // Fetch calendars when mobile is verified
  useEffect(() => {
    if (mobileVerified) {
      setLoadingCalendars(true);
      const calendarsRef = ref(database, 'school/academic/calendars');
      onValue(calendarsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const calendarsArray = Object.entries(data).map(([id, calendar]) => ({
            id,
            ...calendar
          }));
          setCalendars(calendarsArray);
        } else {
          setCalendars([]);
        }
        setLoadingCalendars(false);
      }, (error) => {
        console.error("Error fetching calendars:", error);
        setCalendars([]);
        setLoadingCalendars(false);
      });
    }
  }, [mobileVerified]);

  // Verify mobile number
  const verifyMobile = async (e) => {
    e.preventDefault();
    if (mobileNumber.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const parentsRef = ref(database, 'school/parents');
      const queryRef = query(parentsRef, orderByChild('mobile'), equalTo(mobileNumber));
      
      onValue(queryRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const parentKey = Object.keys(data)[0];
          setParentData({ id: parentKey, ...data[parentKey] });
          setMobileVerified(true);
        } else {
          setError('No data found for this mobile number. Please contact the school administration.');
        }
        setIsLoading(false);
      }, {
        onlyOnce: true
      });
    } catch (err) {
      console.error("Error verifying mobile:", err);
      setError('Failed to verify mobile number. Please try again.');
      setIsLoading(false);
    }
  };

  // Styles
  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    fontFamily: "'Inter', sans-serif",
    display: 'flex',
    flexDirection: 'column',
  };

  const headerStyle = {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
    color: 'white',
    padding: '2rem 2rem',
    textAlign: 'center'
  };

  const contentStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem 0.9rem'
  };

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    padding: '1.4rem',
    marginBottom: '1.5rem'
  };

  const fileItemStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem',
    borderBottom: '1px solid #e2e8f0',
    ':last-child': {
      borderBottom: 'none'
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '1rem',
    outline: 'none',
    marginBottom: '1rem'
  };

  return (
    <>
      <Header/>
      <div style={containerStyle}>
        <header style={headerStyle}>
          <h1 style={{ fontSize: '2rem', fontWeight: '700', margin: 0 }}>School Information Portal</h1>
          <p style={{ margin: '0.5rem 0 0', opacity: 0.9, fontSize: '1rem' }}>
            School calendars and important dates
          </p>
        </header>

        <main style={contentStyle}>
          {!mobileVerified ? (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              padding: '2.5rem',
              maxWidth: '450px',
              margin: '0 auto',
              background: 'linear-gradient(to bottom right, #ffffff, #f9fafb)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '200px',
                height: '200px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0) 70%)',
                zIndex: 0
              }}></div>
              <div style={{
                position: 'absolute',
                bottom: '-30px',
                left: '-30px',
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0) 70%)',
                zIndex: 0
              }}></div>

              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{
                    backgroundColor: '#3b82f6',
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3), 0 2px 4px -1px rgba(59, 130, 246, 0.1)'
                  }}>
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="28" 
                      height="28" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="white" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                  </div>
                </div>

                <h2 style={{ 
                  fontSize: '1.75rem', 
                  fontWeight: '700', 
                  marginBottom: '1rem', 
                  textAlign: 'center',
                  color: '#1e293b',
                  background: 'linear-gradient(to right, #3b82f6, #10b981)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                 Access School Calendars
                </h2>
                
                <p style={{ 
                  color: '#64748b', 
                  textAlign: 'center', 
                  marginBottom: '2rem',
                  fontSize: '0.95rem',
                  lineHeight: '1.5'
                }}>
                  Enter your registered mobile number to access school calendar information
                </p>

                <form onSubmit={verifyMobile}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#f8fafc',
                      borderRadius: '12px',
                      padding: '0 1rem',
                      border: '1px solid #e2e8f0',
                      transition: 'all 0.3s ease',
                      ':focus-within': {
                        borderColor: '#3b82f6',
                        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.2)'
                      }
                    }}>
                      <span style={{
                        color: '#64748b',
                        marginRight: '0.5rem',
                        fontWeight: '500'
                      }}>+91</span>
                      <input
                        type="tel"
                        id="mobile"
                        value={mobileNumber}
                        onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, ''))}
                        placeholder="Enter 10-digit mobile number"
                        maxLength="10"
                        style={{
                          flex: 1,
                          padding: '0.875rem 0',
                          border: 'none',
                          outline: 'none',
                          backgroundColor: 'transparent',
                          fontSize: '1rem',
                          color: '#1e293b'
                        }}
                        required
                      />
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3), 0 2px 4px -1px rgba(59, 130, 246, 0.1)',
                      ':hover': {
                        backgroundColor: '#2563eb',
                        transform: 'translateY(-1px)'
                      },
                      ':active': {
                        transform: 'translateY(0)'
                      },
                      ':disabled': {
                        opacity: 0.7,
                        cursor: 'not-allowed',
                        transform: 'none'
                      }
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg 
                          style={{ 
                            animation: 'spin 1s linear infinite',
                            marginRight: '0.5rem',
                            width: '1.25rem',
                            height: '1.25rem'
                          }} 
                          xmlns="http://www.w3.org/2000/svg" 
                          fill="none" 
                          viewBox="0 0 24 24"
                        >
                          <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Verifying...
                      </div>
                    ) : (
                      'Continue to Portal'
                    )}
                  </button>

                  {error && (
                    <div style={{
                      marginTop: '1.25rem',
                      padding: '0.875rem',
                      backgroundColor: '#fee2e2',
                      color: '#b91c1c',
                      borderRadius: '8px',
                      textAlign: 'center',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        style={{ marginRight: '0.5rem' }}
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                      {error}
                    </div>
                  )}
                </form>
              </div>
            </div>
          ) : (
            <>
              <div style={cardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                      Welcome, {parentData?.name || 'Parent'}
                    </h2>
                    <p style={{ color: '#64748b' }}>Mobile: {parentData?.mobile}</p>
                  </div>
                  <button
                    onClick={() => {
                      setMobileVerified(false);
                      setMobileNumber('');
                      setParentData(null);
                    }}
                    style={{
                      backgroundColor: '#ef4444',
                      color: 'white',
                      padding: '0.5rem 1rem',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Logout
                  </button>
                </div>
              </div>

              <div style={cardStyle}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>School Calendars</h2>
                
                {loadingCalendars ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <svg 
                      style={{ 
                        animation: 'spin 1s linear infinite',
                        width: '2rem',
                        height: '2rem',
                        color: '#3b82f6'
                      }} 
                      xmlns="http://www.w3.org/2000/svg" 
                      fill="none" 
                      viewBox="0 0 24 24"
                    >
                      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : calendars.length > 0 ? (
                  <div>
                    {/* KG Calendar Section */}
                    <div style={{ 
                      backgroundColor: '#f0fdf4',
                      border: '1px solid #86efac',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <h3 style={{ 
                        fontSize: '1rem', 
                        fontWeight: '600', 
                        marginBottom: '1rem',
                        color: '#166534',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <svg 
                          style={{ 
                            width: '1.25rem', 
                            height: '1.25rem', 
                            marginRight: '0.5rem',
                            color: '#16a34a'
                          }} 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                        >
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        KG Section Calendar
                      </h3>
                      {calendars.filter(c => c.type === 'kg').map((calendar, index) => (
                        <div key={index} style={fileItemStyle}>
                          <svg style={{ width: '1.5rem', height: '1.5rem', color: '#16a34a', marginRight: '1rem' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '500' }}>{calendar.fileName}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                              Uploaded on {moment(calendar.uploadedAt).format('MMMM D, YYYY')}
                            </div>
                          </div>
                          <a 
                            href={calendar.fileUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#16a34a',
                              color: 'white',
                              borderRadius: '8px',
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              fontWeight: '500'
                            }}
                          >
                            Download
                          </a>
                        </div>
                      ))}
                      {calendars.filter(c => c.type === 'kg').length === 0 && (
                        <p style={{ color: '#64748b', textAlign: 'center', padding: '1rem' }}>
                          No KG calendar available at the moment
                        </p>
                      )}
                    </div>

                    {/* Primary Calendar Section */}
                    <div style={{ 
                      backgroundColor: '#eff6ff',
                      border: '1px solid #93c5fd',
                      borderRadius: '8px',
                      padding: '1rem'
                    }}>
                      <h3 style={{ 
                        fontSize: '1rem', 
                        fontWeight: '600', 
                        marginBottom: '1rem',
                        color: '#1e40af',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <svg 
                          style={{ 
                            width: '1.25rem', 
                            height: '1.25rem', 
                            marginRight: '0.5rem',
                            color: '#3b82f6'
                          }} 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                        >
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        Primary Section Calendar
                      </h3>
                      {calendars.filter(c => c.type === 'primary').map((calendar, index) => (
                        <div key={index} style={fileItemStyle}>
                          <svg style={{ width: '1.5rem', height: '1.5rem', color: '#3b82f6', marginRight: '1rem' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '500' }}>{calendar.fileName}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                              Uploaded on {moment(calendar.uploadedAt).format('MMMM D, YYYY')}
                            </div>
                          </div>
                          <a 
                            href={calendar.fileUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              borderRadius: '8px',
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              fontWeight: '500'
                            }}
                          >
                            Download
                          </a>
                        </div>
                      ))}
                      {calendars.filter(c => c.type === 'primary').length === 0 && (
                        <p style={{ color: '#64748b', textAlign: 'center', padding: '1rem' }}>
                          No Primary calendar available at the moment
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p style={{ color: '#64748b', textAlign: 'center', padding: '1rem' }}>
                    No school calendars available at the moment. Please check back later.
                  </p>
                )}
              </div>
            </>
          )}
        </main>
      </div>
      <Footer/>
    </>
  );
};

export default SchoolInformationPage;